import React, { useState, useEffect, useCallback } from 'react';
import FeedbackSection from '../components/FeedbackSection';
import { getCurrentUser } from '../services/userService';
import { UserRole } from '../types/user';
import { canUpdateProgress } from '../utils/permissions';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Chip,
  Button,
  Divider,
  List,
  ListItem,
  ListItemText,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Stack,
  Tooltip,
  IconButton
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import PrintIcon from '@mui/icons-material/Print';
import CloseIcon from '@mui/icons-material/Close';
import { ArrowRightAlt } from '@mui/icons-material';
import { ArrowBack, Edit, Update } from '@mui/icons-material';
import { Project, normalizeStatus } from '../types/project';
import { projectService } from '../services/api';
import FishBoneDiagram from '../components/FishBoneDiagram';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, Legend, ResponsiveContainer } from 'recharts';
import UpdateProgress from '../components/UpdateProgress';
import { TestCycle } from '../types/test-cycle';
import { TestCycle as ProjectTestCycle } from '../types/project';
import TopBar from '../components/TopBar';
import {
  isProjectDueForUpdate,
  getProjectDueDate,
  getDaysUntilDue,
  getProjectDueStatus,
  getMissedProgressUpdates,
  getNextRequiredUpdate,
  hasSequentialUpdatesRequired,
  debugProjectCycleSchedule
} from '../utils/projectHelpers';

// Add debugging logger that won't affect rendering
const logWithoutRender = (message: string, data: any) => {

  return null;
};

interface ProjectDetailProps {
  onLogout: () => void;
}

const ProjectDetail = ({ onLogout }: ProjectDetailProps) => {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  
  const [project, setProject] = useState<Project | null>(
    location.state?.project || null
  );
  const [formattedProject, setFormattedProject] = useState<Project | null>(null);
  const [loading, setLoading] = useState(!project);
  const [error, setError] = useState<string>('');
  const [currentUser, setCurrentUser] = useState<{ role: UserRole } | null>(null);
  
  // State for Add Test Cycle dialog
  const [isAddTestCycleOpen, setIsAddTestCycleOpen] = useState(false);

  useEffect(() => {
    if (!project && id) {
      fetchProject();
    } else if (project) {
      // Process and format project data
      formatProjectData(project);
    }
  }, [id, project]);

  // Get current user for role-based access control
  useEffect(() => {
    const user = getCurrentUser();
    setCurrentUser(user);
  }, []);
  
  // Format project data for display
  const formatProjectData = useCallback((projectData: Project) => {
    
    // Create a deep copy to avoid mutation issues
    const formatted = JSON.parse(JSON.stringify(projectData));
    
    // Map API field names to display field names where needed
    if (formatted.startDate && !formatted.firstCycleDate) {
      formatted.firstCycleDate = formatted.startDate;
    }
    
    if (formatted.endDate && !formatted.projectEndDate) {
      formatted.projectEndDate = formatted.endDate;
    }
    
    // Ensure status is properly formatted for display
    formatted.displayStatus = normalizeStatus(formatted.status);
    
    
    // Check for fishbone data presence
    const hasFlatFishboneData = [
      formatted.peopleCauses?.length > 0,
      formatted.processCauses?.length > 0,
      formatted.policyCauses?.length > 0,
      formatted.placeCauses?.length > 0,
      formatted.provisionCauses?.length > 0
    ].some(Boolean);
    
    
    setFormattedProject(formatted);
  }, []);
  

  const fetchProject = async () => {
    if (!id) return;
    
    try {
      setLoading(true);
      const fetchedProject = await projectService.getProjectById(id);
      setProject(fetchedProject);
    } catch (err) {
      console.error('Error fetching project:', err);
      setError('Failed to load project details');
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate('/dashboard'); // Navigate back to dashboard
  };
  
  const handleUpdateProgress = () => {
    setIsAddTestCycleOpen(true);
  };
  
  const handleTestCycleClose = () => {
    setIsAddTestCycleOpen(false);
  };
  
  const handleEdit = () => {
    // Navigate to AddProject page with the current project data for editing
    if (project) {
      navigate('/add-project', { state: { projectToEdit: project } });
    }
  };


  const handleTestCycleSubmit = async (testCycle: TestCycle) => {
    try {
      // Update project with the new test cycle/progress update
      if (project && project._id) {
        setLoading(true);
        
        // Use testCycleService to create the test cycle
        const { testCycleService } = require('../services/api');
        const createdCycle = await testCycleService.createTestCycle(project._id, testCycle);
        await fetchProject();
      }
    } catch (err) {
      console.error('Error adding progress update:', err);
      setError('Failed to add progress update');
    } finally {
      setLoading(false);
      setIsAddTestCycleOpen(false);
    }
  };

  const formatDate = (dateString: string | Date) => {
    if (!dateString) return 'N/A';
    if (typeof dateString === 'string' && dateString.trim() === '') return 'N/A';
    
    
    try {
      // Handle different date formats
      let date: Date;
      
      // If it's already a Date object
      if (dateString instanceof Date) {
        date = dateString;
      }
      // If it's a string that might be in dd/mm/yyyy format
      else if (typeof dateString === 'string' && dateString.includes('/')) {
        const parts = dateString.split('/');
        if (parts.length === 3) {
          // Assume dd/mm/yyyy format and convert to mm/dd/yyyy for Date constructor
          const [day, month, year] = parts;
          date = new Date(`${month}/${day}/${year}`);
        } else {
          date = new Date(dateString);
        }
      }
      // Standard date string
      else {
        date = new Date(dateString);
      }
      
      // Check if the date is valid
      if (isNaN(date.getTime())) {
        console.warn('Invalid date after parsing:', dateString);
        return 'Invalid Date';
      }
      
      // Additional check for unrealistic dates
      const year = date.getFullYear();
      if (year < 1900 || year > 2100) {
        console.warn('Date year out of range:', year, 'for date:', dateString);
        return 'Invalid Date';
      }
      
      return date.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric' 
      });
    } catch (error) {
      console.warn('Date formatting error:', error, 'for date string:', dateString);
      return 'Invalid Date';
    }
  };

  const getStatusColor = (status: string) => {
    const lowerStatus = status.toLowerCase();
    switch (lowerStatus) {
      case 'ongoing': return 'primary';
      case 'completed': return 'success';
      case 'past_completion': return 'success';
      case 'near_completion': return 'info';
      case 'paused': return 'warning';
      case 'cancelled': return 'error';
      case 'draft': return 'default';
      default: return 'default';
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Paper elevation={3} sx={{ p: 4, textAlign: 'center' }}>
          <CircularProgress />
          <Typography variant="body1" sx={{ mt: 2 }}>
            Loading project details...
          </Typography>
        </Paper>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Paper elevation={3} sx={{ p: 4 }}>
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
          <Button onClick={handleBack} startIcon={<ArrowBack />}>
            Back to Projects
          </Button>
        </Paper>
      </Container>
    );
  }

  if (!project) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Paper elevation={3} sx={{ p: 4 }}>
          <Typography variant="body1" align="center" color="textSecondary">
            Project not found
          </Typography>
          <Box sx={{ textAlign: 'center', mt: 2 }}>
            <Button onClick={handleBack} startIcon={<ArrowBack />}>
              Back to Projects
            </Button>
          </Box>
        </Paper>
      </Container>
    );
  }


  return (
    <>
      <style>
        {`
          @media print {
            .no-print {
              display: none !important;
            }
            @page {
              margin: 1cm;
              size: A4;
            }
            
            /* Landscape for fishbone page */
            @page :has(.fishbone-container) {
              size: A4 landscape;
            }
            
            /* Prevent overlapping */
            body {
              print-color-adjust: exact;
              -webkit-print-color-adjust: exact;
            }
            
            /* Force single column layout for print */
            .MuiGrid-container {
              display: block !important;
            }
            
            .MuiGrid-item {
              max-width: 100% !important;
              flex-basis: 100% !important;
            }
            
            /* Add page breaks before major sections */
            .print-page-break {
              page-break-before: always;
              break-before: page;
            }
            
            /* Prevent charts and diagrams from breaking across pages */
            .print-no-break {
              page-break-inside: avoid;
              break-inside: avoid;
            }
            
            /* Ensure charts fit on page */
            .recharts-wrapper {
              max-height: 400px !important;
            }
            
            /* Fix sticky positioning in print */
            .MuiCard-root {
              position: relative !important;
            }
            
            /* Fishbone diagram print fixes */
            svg {
              max-width: 100% !important;
              height: auto !important;
              page-break-inside: avoid;
            }
            
            /* Ensure fishbone container fits */
            .fishbone-container {
              overflow: visible !important;
              max-width: 100% !important;
            }
            
            /* Scale down large diagrams */
            .print-no-break svg {
              transform: scale(0.9);
              transform-origin: top left;
            }
          }
        `}
      </style>
      <Box sx={{ flexGrow: 1 }} className="no-print">
        <TopBar onLogout={onLogout} />
      </Box>
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Paper elevation={3} sx={{ p: 4 }}>
          {/* Header */}
          <Box sx={{ mb: 4 }}>
            <Button 
              onClick={handleBack} 
              startIcon={<ArrowBack />}
              sx={{ mb: 2 }}
              className="no-print"
            >
              Back to Projects
            </Button>
            
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
              <Box>
                <Typography variant="h4" gutterBottom>
                  {project.projectTitle}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                  <Chip 
                    label={
                      project.status === 'archived' 
                        ? 'Shared on Learning Platform' 
                        : project.status === 'completed'
                        ? 'Completed'
                        : formattedProject?.displayStatus || project.status
                    } 
                    color={getStatusColor(project.status) as any}
                    sx={{
                      backgroundColor: 
                        project.status === 'archived' ? '#2196F3' : 
                        project.status === 'completed' ? '#4CAF50' : undefined,
                      color: 
                        project.status === 'archived' || project.status === 'completed' ? 'white' : undefined,
                      fontWeight: 'bold'
                    }}
                  />
                  {/* Show progress update status for ongoing projects */}
                  {project.status === 'ongoing' && project.measurementFrequency && (() => {
                    const dueStatus = getProjectDueStatus(project);
                    const isDue = isProjectDueForUpdate(project, 3);
                    const daysUntil = getDaysUntilDue(project);
                    const isOverdue = daysUntil !== null && daysUntil < 0;
                    
                    if (isDue) {
                      return (
                        <Chip 
                          label={dueStatus.status}
                          size="small"
                          color={dueStatus.color === 'error' ? 'error' : 'warning'}
                          sx={{ 
                            fontWeight: 'bold', 
                            animation: dueStatus.color === 'error' ? 'pulse 2s infinite' : 'none',
                            backgroundColor: dueStatus.color === 'error' ? undefined : '#ffeb3b',
                            color: dueStatus.color === 'error' ? undefined : '#333',
                            '@keyframes pulse': {
                              '0%': { boxShadow: '0 0 0 0 rgba(244, 67, 54, 0.7)' },
                              '70%': { boxShadow: '0 0 0 10px rgba(244, 67, 54, 0)' },
                              '100%': { boxShadow: '0 0 0 0 rgba(244, 67, 54, 0)' }
                            }
                          }}
                        />
                      );
                    }
                    return null;
                  })()}
                </Box>
                {/* Show sequential progress update information for ongoing projects */}
                {project.status === 'ongoing' && project.measurementFrequency && (() => {
                  // Debug the project cycle schedule
                  debugProjectCycleSchedule(project);
                  
                  const missedUpdates = getMissedProgressUpdates(project);
                  const nextRequired = getNextRequiredUpdate(project);
                  const hasMultiple = hasSequentialUpdatesRequired(project);
                  
                  if (missedUpdates.length === 0) {
                    // Calculate the proper next due date based on cycle schedule
                    const nextDueDate = getProjectDueDate(project);
                    const nextDueDateStr = nextDueDate ? nextDueDate.toLocaleDateString() : 'TBD';
                    
                    // Check if project hasn't started yet
                    const projectStartDate = project.firstCycleDate || project.startDate || project.createdAt;
                    const startDate = new Date(projectStartDate);
                    const now = new Date();
                    const isProjectNotStarted = startDate > now;
                    
                    return (
                      <Alert severity={isProjectNotStarted ? "info" : "success"} sx={{ mb: 2 }}>
                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                          {isProjectNotStarted ? "📅 Project Not Started Yet" : "✅ All Progress Updates Current"}
                        </Typography>
                        <Typography variant="body2">
                          {isProjectNotStarted ? 
                            `Project starts: ${startDate.toLocaleDateString()}` :
                            `Next update due: ${nextDueDateStr}`
                          }
                        </Typography>
                        <Typography variant="body2">
                          {isProjectNotStarted ? 
                            `First cycle due: ${nextDueDateStr}` :
                            ""
                          }
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Measurement Frequency: {project.measurementFrequency}
                        </Typography>
                      </Alert>
                    );
                  }
                  
                  if (hasMultiple) {
                    return (
                      <Alert 
                        severity="warning"
                        sx={{ 
                          mb: 2,
                          backgroundColor: '#fff3e0',
                          border: '1px solid #ff9800'
                        }}
                      >
                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                          📅 Multiple Cycles Available for Completion
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 1 }}>
                          <strong>Start with:</strong> {nextRequired?.toLocaleDateString()} 
                          <Chip 
                            label="Cycle 1" 
                            size="small" 
                            color="warning" 
                            sx={{ ml: 1 }}
                          />
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 1 }}>
                          <strong>Available Cycles:</strong> {missedUpdates.length} cycles ready for completion
                        </Typography>
                        <Box sx={{ mt: 1 }}>
                          <Typography variant="caption" color="text.secondary">
                            Cycle Dates: {missedUpdates.map((d, index) => `${index + 1}. ${d.toLocaleDateString()}`).join(', ')}
                          </Typography>
                        </Box>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                          💡 You can complete all available cycles, starting with the earliest
                        </Typography>
                      </Alert>
                    );
                  } else if (nextRequired) {
                    const now = new Date();
                    const daysOverdue = Math.ceil((now.getTime() - nextRequired.getTime()) / (1000 * 3600 * 24));
                    
                    return (
                      <Alert 
                        severity="error"
                        sx={{ 
                          mb: 2,
                          backgroundColor: '#ffebee',
                          border: '1px solid #f44336'
                        }}
                      >
                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                          📅 Progress Update Required
                        </Typography>
                        <Typography variant="body2">
                          Due Date: {nextRequired.toLocaleDateString()}
                        </Typography>
                        <Typography variant="body2">
                          {daysOverdue > 0 ? `Overdue by ${daysOverdue} days` : 'Due Today'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Measurement Frequency: {project.measurementFrequency}
                        </Typography>
                      </Alert>
                    );
                  }
                  
                  return null;
                })()}
                
                {/* Show completion summary for completed projects */}
                {project.status === 'completed' && project.testCycles && project.testCycles.length > 0 && (() => {
                  // Find the test cycle that achieved the target
                  const completingCycle = (project.testCycles as any[]).find((cycle: any) => {
                    const num = cycle.numerator ?? cycle.metrics?.numerator ?? 0;
                    const den = cycle.denominator ?? cycle.metrics?.denominator ?? 0;
                    if (!num || !den || den === 0) return false;
                    const achievedPercentage = (num / den) * 100;
                    return achievedPercentage >= project.improvementTo;
                  });
                  
                  if (completingCycle) {
                    const cNum = completingCycle.numerator ?? completingCycle.metrics?.numerator ?? 0;
                    const cDen = completingCycle.denominator ?? completingCycle.metrics?.denominator ?? 0;
                    const achievedPercentage = (cNum / cDen) * 100;
                    const completionDate = new Date(completingCycle.createdAt || completingCycle.cycleResolutionDate || new Date());
                    
                    return (
                      <Alert 
                        severity="success"
                        sx={{ 
                          mb: 2,
                          backgroundColor: '#e8f5e8',
                          border: '1px solid #4caf50'
                        }}
                      >
                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                          🎉 Project Completed Successfully!
                        </Typography>
                        <Typography variant="body2">
                          <strong>Target Achieved:</strong> {achievedPercentage.toFixed(1)}% (Target: {project.improvementTo}%)
                        </Typography>
                        <Typography variant="body2">
                          <strong>Completion Date:</strong> {completionDate.toLocaleDateString()}
                        </Typography>
                        <Typography variant="body2">
                          <strong>Final Results:</strong> {completingCycle.numerator}/{completingCycle.denominator}
                        </Typography>
                      </Alert>
                    );
                  }
                  return null;
                })()}
              </Box>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {/* Show Update Progress button when project has required updates */}
                {project.status === 'ongoing' && 
                 project.measurementFrequency && 
                 canUpdateProgress() && 
                 getNextRequiredUpdate(project) && (
                  <Button 
                    startIcon={<Update />} 
                    variant="contained"
                    color="primary"
                    sx={{ 
                      mr: 1,
                      animation: getDaysUntilDue(project) !== null && getDaysUntilDue(project)! < 0 ? 'pulse 2s infinite' : 'none',
                      fontWeight: 'bold',
                      '@keyframes pulse': {
                        '0%': { boxShadow: '0 0 0 0 rgba(25, 118, 210, 0.7)' },
                        '70%': { boxShadow: '0 0 0 10px rgba(25, 118, 210, 0)' },
                        '100%': { boxShadow: '0 0 0 0 rgba(25, 118, 210, 0)' }
                      }
                    }}
                    onClick={() => setIsAddTestCycleOpen(true)}
                  >
                    {(() => {
                      const hasMultiple = hasSequentialUpdatesRequired(project);
                      const nextRequired = getNextRequiredUpdate(project);
                      if (hasMultiple && nextRequired) {
                        return `Update Progress (${nextRequired.toLocaleDateString()})`;
                      }
                      return 'Update Progress';
                    })()}
                  </Button>
                )}
                
                {/* Only show Edit button for users with update permission */}
                {canUpdateProgress() ? (
                  <Button 
                    startIcon={<Edit />} 
                    sx={{ mr: 1 }}
                    onClick={handleEdit}
                  >
                    Edit
                  </Button>
                ) : (
                  <Button 
                    startIcon={<Edit />} 
                    sx={{ mr: 1 }}
                    disabled
                    title="Only facility users can edit projects"
                  >
                    Edit
                  </Button>
                )}
              </Box>
            </Box>
          </Box>

          {/* Project Details Section with Progress Chart */}
          <Grid container spacing={3} sx={{ mt: 3 }}>
            {/* Left - Project Details */}
            <Grid item xs={12} md={7}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    📋 Project Details
                  </Typography>
                  <Divider sx={{ mb: 3 }} />
              
              {/* Problem Statement */}
              {project.problemStatement && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold' }}>
                    Problem Statement:
                  </Typography>
                  <Typography variant="body2" sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                    {project.problemStatement}
                  </Typography>
                </Box>
              )}
              
              {/* Domain and Sub Domain */}
              <Grid container spacing={3} sx={{ mb: 3 }}>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold' }}>
                    Domain:
                  </Typography>
                  <Typography variant="body2">
                    {project.domain || (project as any).domain || 'Not specified'}
                  </Typography>
                </Grid>
                
                {/* Project Start Date */}
                {(project.firstCycleDate || (project as any).firstCycleDate) && (
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold' }}>
                      Project Start Date:
                    </Typography>
                    <Typography variant="body2">
                      {project.firstCycleDate || (project as any).firstCycleDate}
                    </Typography>
                  </Grid>
                )}
                
                {(project.subDomain || (project as any).subDomain) && (
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold' }}>
                      Sub Domain:
                    </Typography>
                    <Typography variant="body2">
                      {project.subDomain || (project as any).subDomain}
                    </Typography>
                  </Grid>
                )}
                
                {/* Project End Date and Project Lead in same column */}
                <Grid item xs={12} md={6}>
                  {(project.projectEndDate || (project as any).projectEndDate) && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold' }}>
                        Project End Date:
                      </Typography>
                      <Typography variant="body2">
                        {project.projectEndDate || (project as any).projectEndDate}
                      </Typography>
                    </Box>
                  )}
                  
                  {(project.responsiblePerson || (project as any).responsiblePerson) && (
                    <Box>
                      <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold' }}>
                        Project Lead:
                      </Typography>
                      <Typography variant="body2">
                        {project.responsiblePerson || (project as any).responsiblePerson}
                      </Typography>
                    </Box>
                  )}
                </Grid>
              </Grid>
              
              {/* Indicator Description */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold' }}>
                  Indicator Description:
                </Typography>
                <Typography variant="body2" sx={{ mb: 2 }}>
                  {(() => {
                    // If using predefined KPI, show the actual KPI name from MOH 515
                    if (!project.useCustomIndicator && (project.selectedKPI || (project as any).selectedKPI)) {
                      const kpiDescriptions: Record<string, string> = {
                        'total_households': 'Total number of households',
                        'households_visited_new': 'Total number of households visited by CHVs in the month(New Visit)',
                        'households_visited_revisit': 'Total number of households visited in the month by CHVs(Revisit)',
                        'households_health_insurance_new': 'Total number of households visited in the Month with upto date health insuarance(New Visit)',
                        'households_uhc_new': 'Total number of households visited in the Month with upto date health insuarance-UHC (New Visit)',
                        'households_nhif_new': 'Total number of households visited in the Month with upto date health insuarance-NHIF(New Visit)',
                        'households_others_new': 'Total number of households visited in the Month with upto date health insuarance-OTHERS (New Visit)',
                        'households_safe_water_new': 'Total number of NEW households visited in the month accessing safe water (New Visit)',
                        'households_handwashing_new': 'Number of households with hand washing facilities e.g. leaky tins in use (New Visit)',
                        'households_latrines_new': 'Number of households with functional latrines(New Visit)',
                        'households_refuse_disposal_new': 'Number of households with Refuse disposal facility(New Visit)',
                        'women_fp_counselled': 'Number of women 15 -49yrs counselled on FP methods',
                        'women_fp_commodities': 'Number of women 15-49yrs provided with FP commodities',
                        'women_fp_referred': 'Number of women 15-49yrs Referred for FP Services',
                        'pregnant_women': 'Number of women who are pregnant',
                        'underage_pregnancies': 'Number of new under-age pregnancies (under 18 years)',
                        'pregnant_anc_counselled': 'Number of pregnant women counselled on ANC services',
                        'pregnant_referred': 'Number of pregnant women Referred to health facility',
                        'deliveries_cu': 'Number of New deliveries that took place in C.U',
                        'deliveries_facility': 'Number of New deliveries that took place in Health Facility',
                        'deliveries_home': 'Number of New deliveries that took place at Home',
                        'underage_deliveries': 'Number of New under-age Deliveries (10-19 years) in the month',
                        'mothers_visited_48hrs': 'Number of new mothers visited within 48 hrs of delivery',
                        'newborns_visited_48hrs': 'Number of new-borns visited at home within 48 hours of delivery',
                        'home_delivery_pnc_referred': 'Home delivery referred for Post Natal Care (PNC) Services',
                        'child_muac_red': 'Child 6-59 months with MUAC (Red) indicating severe malnutrition',
                        'child_muac_yellow': 'Child 6-59 months with MUAC (Yellow) indicating moderate malnutrition',
                        'children_dewormed': 'Number of children 12-59 months dewormed',
                        'child_vitamin_a_referred': 'Child 6-59 months referred for Vitamin A supplementation',
                        'children_immunization_referred': 'Children of 0-11 months referred for immunization',
                        'immunization_defaulter_referred': 'Immunization defaulter referred',
                        'children_developmental_milestones_referred': 'Children with delayed developmental milestones referred',
                        'hts_referred': 'Referred for HIV Testing Services (HTS)',
                        'art_defaulter': 'ART defaulter in the Month',
                        'hiv_exposed_infant_defaulters': 'HIV exposed infant defaulters In the month',
                        'art_defaulter_traced': 'ART defaulter traced and referred',
                        'hiv_exposed_infant_traced': 'HIV exposed infant defaulters traced and referred',
                        'tb_screened': 'Number of persons screened for TB',
                        'presumptive_tb_referred': 'Number of presumptive TB persons referred for TB diagnosis',
                        'tb_confirmed_cases': 'Number of bacteriologically confirmed TB casesFrom TB clinic)',
                        'tb_contacts_referred': 'Number of presumptive TB contacts of bacteriologically confirmed TB cases referred for Screening',
                        'tb_contacts_children_ipt': 'Number of children <5 years TB contacts of bacteriologically confirmed TB cases referred for IPT',
                        'tb_treatment_interrupters': 'Number of TB Treatment Interruptersin the Month(TB clinic)',
                        'tb_interrupters_traced': 'Number of TB Treatment Interrupters traced',
                        'diabetes_referred': 'Known cases of Diabetes referred',
                        'cancer_referred': 'Known cases of Cancer referred',
                        'hypertension_referred': 'Known cases of Hypertension referred',
                        'mental_illness_referred': 'Known cases of Mental Illness referred',
                        'older_persons_referred': 'Older persons (60 years or more) referred for comprehensive geriatric services (Check ups)',
                        'fp_services_reached': 'Number of women referred for FP Services reaching the health facility',
                        'anc_services_reached': 'Number of Pregnant Women referred for ANC services reaching the health facility',
                        'pnc_services_reached': 'Number of women referred for Post natal services reaching the health facility',
                        'immunization_defaulters_reached': 'Number of Immunization defaulters referred reaching the health facility',
                        'hts_reached': 'Number of people referred for HIV testing services reaching the health facility',
                        'art_defaulters_reached': 'Number of ART Defaulters referred reaching the health facility',
                        'hiv_exposed_infant_reached': 'Number of HIV exposed Infant defaulters reaching the health facility',
                        'older_persons_reached': 'Number of older persons referred for routine check up reaching the health facility',
                        'presumptive_tb_reached': 'Number of Presumptive TB cases referred reaching the health facility',
                        'tb_contacts_reached': 'Number of Presumptive TB contacts referred reaching the health facility',
                        'tb_contacts_ipt_reached': 'Number of <5 Child TB contacts for Isoniazid Preventive Therapy referred reaching the health facility',
                        'tb_interruptors_reached': 'Number of TB Interruptors referred reaching the health facility',
                        'under5_fever_cases': 'Number of under 5 years cases with fever for less than 7 days',
                        'under5_fever_rdt': 'Number of under 5 years cases with fever for less than 7 days with RDT done',
                        'under5_fever_rdt_positive': 'Number of under 5 years cases with fever for less than 7 days with (RDT +ve) results',
                        'under5_malaria_act': 'Number of under 5 years Malaria Cases (RDT +ve) treated with ACT',
                        'children_fast_breathing': 'Number of children aged 2-59 months presenting with fast breathing',
                        'children_fast_breathing_amoxycillin': 'Number of children aged 2-59 months presenting with fast breathing treated with Amoxycillin(DT)',
                        'children_diarrhoea': 'Number of cases of diarrhoea identified in children 2-59 months age',
                        'children_diarrhoea_zinc_ors': 'Number of children of 2-59 months with diarrhoea treated with Zinc and ORS',
                        'over5_fever_cases': 'Number of over 5 years cases with fever for less than 7 days',
                        'over5_fever_rdt': 'Number of over 5 years cases with fever for less than 7 days with RDT done',
                        'over5_fever_rdt_positive': 'Number of over 5 years cases with fever for less than 7 days with (RDT +ve) results',
                        'over5_malaria_act': 'Number of over 5 years Malaria Cases (RDT +ve) treated with ACT',
                        'community_dialogue_days': 'Total number of community dialogue days held',
                        'community_action_days': 'Total number of community Action Days held',
                        'community_units_meetings': 'Total number of community units monthly meetings held',
                        'deaths_0_28_days': 'No. of deaths in the month, 0-28 days',
                        'deaths_29_days_11_months': 'No. of deaths in the month, 29 days-11 months',
                        'deaths_12_59_months': 'No. of deaths in the month, 12-59 months',
                        'deaths_6_59_years': 'No. of deaths in the month, 6-59 years(not maternal)',
                        'deaths_60_years_above': 'No. of deaths in the month, 60 years and above',
                        'maternal_deaths': 'No. of Maternal deaths in the month',
                        // Legacy KPI names
                        'skilled_delivery': 'Number of New deliveries that took place in Health Facility',
                        'anc_coverage': 'Number of Pregnant Women referred for ANC services reaching the health facility',
                        'immunization_coverage': 'Number of Immunization defaulters referred reaching the health facility',
                        'malaria_treatment': 'Number of under 5 years Malaria Cases (RDT +ve) treated with ACT',
                        'tb_detection': 'Number of bacteriologically confirmed TB casesFrom TB clinic)',
                        'hiv_testing': 'Number of people referred for HIV testing services reaching the health facility',
                        'family_planning': 'Number of women referred for FP Services reaching the health facility',
                        'nutrition_screening': 'Child 6-59 months with MUAC (Red) indicating severe malnutrition',
                        'mental_health': 'Known cases of Mental Illness referred',
                        'reproductive_health': 'Number of women referred for Post natal services reaching the health facility'
                      };
                      const selectedKPI = project.selectedKPI || (project as any).selectedKPI;
                      return kpiDescriptions[selectedKPI] || project.indicatorDescription || selectedKPI;
                    }
                    // For custom indicators, show the custom description
                    return project.indicatorDescription || 'No indicator description provided';
                  })()}
                </Typography>
              </Box>
              
              {/* Numerator and Denominator */}
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} md={6}>
                  <Box sx={{ p: 2, bgcolor: 'rgba(76, 175, 80, 0.08)', borderRadius: 1 }}>
                    <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold' }}>
                      📈 Numerator:
                    </Typography>
                    <Typography variant="body2">
                      {project.indicatorNumerator || (project as any).numerator || 'Not specified'}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Box sx={{ p: 2, bgcolor: 'rgba(255, 152, 0, 0.08)', borderRadius: 1 }}>
                    <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold' }}>
                      📊 Denominator:
                    </Typography>
                    <Typography variant="body2">
                      {project.indicatorDenominator || (project as any).denominator || 'Not specified'}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
              
              {/* Target Improvement */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold' }}>
                  🎯 Target Improvement:
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 2, bgcolor: 'rgba(0, 0, 0, 0.04)', borderRadius: 1 }}>
                  <Typography variant="h6" sx={{ color: 'text.secondary' }}>
                    From {(() => {
                      // Calculate current percentage from most recent test cycle OR initial values
                      if (project.testCycles && project.testCycles.length > 0) {
                        // Use latest test cycle data
                        const latestCycle = [...project.testCycles]
                          .sort((a, b) => {
                            const cycleA = a as any;
                            const cycleB = b as any;
                            const dateA = a.endDate || a.startDate || cycleA.createdAt || '';
                            const dateB = b.endDate || b.startDate || cycleB.createdAt || '';
                            const timeA = dateA ? new Date(dateA).getTime() : 0;
                            const timeB = dateB ? new Date(dateB).getTime() : 0;
                            return timeB - timeA;
                          })[0];
                        
                        const cycleData = latestCycle as any;
                        const cNum = cycleData.numerator ?? cycleData.metrics?.numerator ?? 0;
                        const cDen = cycleData.denominator ?? cycleData.metrics?.denominator ?? 0;
                        if (cDen !== 0) {
                          const percentage = (cNum / cDen) * 100;
                          return percentage.toFixed(1);
                        }
                      }
                      
                      // No test cycles yet - calculate from initial indicator values
                      const numerator = parseFloat(project.indicatorNumerator || '0');
                      const denominator = parseFloat(project.indicatorDenominator || '1');
                      if (denominator !== 0) {
                        const percentage = (numerator / denominator) * 100;
                        return percentage.toFixed(1);
                      }
                      
                      return project.improvementFrom || 0;
                    })()}%
                  </Typography>
                  <ArrowRightAlt sx={{ fontSize: 32 }} />
                  <Typography variant="h6" fontWeight="bold" sx={{ color: 'success.main' }}>
                    To {project.improvementTo || 100}%
                  </Typography>
                </Box>
              </Box>
              
            </CardContent>
          </Card>
            </Grid>
            
            {/* Right Sidebar - Project Progress */}
            <Grid item xs={12} md={5}>
              <Card sx={{ position: 'sticky', top: 20 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', fontSize: '1rem' }}>
                    📈 Project Progress
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  
                  <Typography variant="subtitle2" gutterBottom sx={{ fontSize: '0.875rem', fontWeight: 'bold' }}>
                    Project Progress Since Project Start
                  </Typography>
                  
                  {(() => {
                    const chartData = [];
                    
                    // Calculate baseline percentage from indicatorNumerator/indicatorDenominator
                    const baseNum = parseFloat(project.indicatorNumerator || '0');
                    const baseDen = parseFloat(project.indicatorDenominator || '0');
                    const baselinePct = baseDen > 0
                      ? parseFloat(((baseNum / baseDen) * 100).toFixed(1))
                      : (project.improvementFrom || 0);
                    
                    // Add initial baseline point with DD/MM/YYYY date parsing
                    const rawStart = project.firstCycleDate || project.startDate || project.createdAt;
                    if (rawStart) {
                      const parts = String(rawStart).match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
                      const baselineDate = parts
                        ? new Date(+parts[3], +parts[2] - 1, +parts[1])
                        : new Date(rawStart);
                      if (!isNaN(baselineDate.getTime())) {
                        chartData.push({
                          date: baselineDate.toLocaleDateString('en-US', { month: 'short', year: 'numeric' }),
                          percentage: baselinePct,
                          isBaseline: true
                        });
                      } else {
                        chartData.push({
                          date: 'Start',
                          percentage: baselinePct,
                          isBaseline: true
                        });
                      }
                    }
                    
                    // Add progress updates from test cycles
                    if (project.testCycles && project.testCycles.length > 0) {
                      project.testCycles.forEach((cycle: any, index: number) => {
                        // Calculate percentage: check multiple possible field locations
                        const num = cycle.numerator ?? cycle.metrics?.numerator ?? cycle.metrics?.count ?? 0;
                        const den = cycle.denominator ?? cycle.metrics?.denominator ?? cycle.metrics?.total ?? 0;
                        let performance = 0;
                        if (den > 0) {
                          performance = (num / den) * 100;
                        } else if (cycle.metrics?.performance) {
                          performance = cycle.metrics.performance;
                        }
                        
                        // Try multiple date fields
                        const dateValue = cycle.startDate || cycle.createdAt || cycle.endDate || (cycle as any).date;
                        let dateLabel = `Update ${index + 1}`;
                        
                        if (dateValue) {
                          try {
                            const cycleDate = new Date(dateValue);
                            if (!isNaN(cycleDate.getTime())) {
                              dateLabel = cycleDate.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
                            }
                          } catch (e) {
                            // Use default Update label
                          }
                        }
                        
                        const percentageValue = parseFloat(performance.toFixed(1));
                        chartData.push({
                          date: dateLabel,
                          percentage: percentageValue, // Keep 1 decimal place
                          isBaseline: false
                        });
                        
                      });
                    }
                    

                    
                    // If no data, show message
                    if (chartData.length === 0) {
                      return (
                        <Box sx={{ width: '100%', height: 300, mt: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'grey.50', borderRadius: 1 }}>
                          <Typography variant="body2" color="text.secondary">
                            No progress data available yet
                          </Typography>
                        </Box>
                      );
                    }
                    
                    return (
                      <Box sx={{ width: '100%', height: 300, mt: 2 }}>
                        <ResponsiveContainer width="100%" height="100%">
                          <LineChart
                            data={chartData}
                            margin={{ top: 5, right: 5, left: 0, bottom: 5 }}
                          >
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis 
                              dataKey="date" 
                              tick={{ fontSize: 10 }}
                              angle={-45}
                              textAnchor="end"
                              height={60}
                            />
                            <YAxis 
                              label={{ value: 'Percentage (%)', angle: -90, position: 'insideLeft', style: { fontSize: 10 } }}
                              tick={{ fontSize: 10 }}
                              domain={[(dataMin: number) => Math.max(0, Math.floor(dataMin / 5) * 5), 100]}
                            />
                            <RechartsTooltip 
                              formatter={(value: any) => `${value}%`}
                              labelFormatter={(label: any) => `Date: ${label}`}
                            />
                            <Line 
                              type="monotone" 
                              dataKey="percentage" 
                              stroke="#1976d2" 
                              strokeWidth={3}
                              dot={{ fill: '#1976d2', r: 6, strokeWidth: 2, stroke: '#fff' }}
                              activeDot={{ r: 8, fill: '#1976d2' }}
                              name="Progress"
                              connectNulls
                            />
                            {/* Target line */}
                            <Line 
                              type="monotone" 
                              dataKey={() => project.improvementTo || 100}
                              stroke="#4caf50" 
                              strokeWidth={2}
                              strokeDasharray="5 5"
                              dot={false}
                              name="Target"
                            />
                          </LineChart>
                        </ResponsiveContainer>
                      </Box>
                    );
                  })()}
                  
                  {/* Legend */}
                  <Box sx={{ mt: 2, display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Box sx={{ width: 16, height: 3, bgcolor: '#1976d2' }} />
                      <Typography variant="caption">Progress</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Box sx={{ width: 16, height: 3, bgcolor: '#4caf50', borderTop: '2px dashed #4caf50' }} />
                      <Typography variant="caption">Target ({project.improvementTo || 100}%)</Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Fishbone Analysis Section */}
          {(project.rootCauseAnalysisTool?.toLowerCase() === 'fishbone' || (project as any).showFishBoneDiagram) && (
            <Card sx={{ mt: 3 }} className="print-page-break print-no-break">
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  🐟 Fishbone Analysis
                </Typography>
                <Divider sx={{ mb: 3 }} />
                
                <Box sx={{ mb: 4 }} className="fishbone-container">
                  <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 1, p: 2, bgcolor: '#fafafa', overflow: 'visible' }}>
                    <FishBoneDiagram 
                      fishBoneAnalysis={{
                        peopleCauses: project.fishBoneAnalysis?.peopleCauses || 
                          (((project as any).peopleCauses || (project as any).people) && ((project as any).peopleCauses || (project as any).people).length > 0 ? 
                            { 
                              causes: ((project as any).peopleCauses || (project as any).people).map((c: any) => c.text || '').filter(Boolean).join('\n'),
                              causeItems: (project as any).peopleCauses || (project as any).people
                            } as any : 
                            { causes: '', causeItems: [] }),
                        processCauses: project.fishBoneAnalysis?.processCauses || 
                          (((project as any).processCauses || (project as any).process) && ((project as any).processCauses || (project as any).process).length > 0 ? 
                            { 
                              causes: ((project as any).processCauses || (project as any).process).map((c: any) => c.text || '').filter(Boolean).join('\n'),
                              causeItems: (project as any).processCauses || (project as any).process 
                            } as any : 
                            { causes: '', causeItems: [] }),
                        policyCauses: project.fishBoneAnalysis?.policyCauses || 
                          (((project as any).policyCauses || (project as any).policy) && ((project as any).policyCauses || (project as any).policy).length > 0 ? 
                            { 
                              causes: ((project as any).policyCauses || (project as any).policy).map((c: any) => c.text || '').filter(Boolean).join('\n'),
                              causeItems: (project as any).policyCauses || (project as any).policy 
                            } as any : 
                            { causes: '', causeItems: [] }),
                        placeCauses: project.fishBoneAnalysis?.placeCauses || 
                          (((project as any).placeCauses || (project as any).place) && ((project as any).placeCauses || (project as any).place).length > 0 ? 
                            { 
                              causes: ((project as any).placeCauses || (project as any).place).map((c: any) => c.text || '').filter(Boolean).join('\n'),
                              causeItems: (project as any).placeCauses || (project as any).place 
                            } as any : 
                            { causes: '', causeItems: [] }),
                        provisionCauses: project.fishBoneAnalysis?.provisionCauses || 
                          (((project as any).provisionCauses || (project as any).provision) && ((project as any).provisionCauses || (project as any).provision).length > 0 ? 
                            { 
                              causes: ((project as any).provisionCauses || (project as any).provision).map((c: any) => c.text || '').filter(Boolean).join('\n'),
                              causeItems: (project as any).provisionCauses || (project as any).provision 
                            } as any : 
                            { causes: '', causeItems: [] }),
                      }}
                      problemStatement={project.problemStatement || 'Problem Statement'}
                      projectTitle={project.projectTitle || 'QI Project Title'}
                    />
                  </Box>
                </Box>
                
                {/* Display all causes by category */}
                {(() => {
                  const peopleCauses = (project as any).peopleCauses || (project as any).people || [];
                  const processCauses = (project as any).processCauses || (project as any).process || [];
                  const policyCauses = (project as any).policyCauses || (project as any).policy || [];
                  const placeCauses = (project as any).placeCauses || (project as any).place || [];
                  const provisionCauses = (project as any).provisionCauses || (project as any).provision || [];
                  
                  const hasAnyCauses = peopleCauses.length > 0 || processCauses.length > 0 || 
                                      policyCauses.length > 0 || placeCauses.length > 0 || 
                                      provisionCauses.length > 0;
                  
                  if (!hasAnyCauses) return null;
                  
                  return (
                    <Box sx={{ mt: 4 }}>
                      <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                        Identified Causes by Category
                      </Typography>
                      <Grid container spacing={2} sx={{ mt: 1 }}>
                        {/* People Causes */}
                        {peopleCauses.length > 0 && (
                          <Grid item xs={12} md={6}>
                            <Paper sx={{ p: 2, bgcolor: 'rgba(33, 150, 243, 0.05)', borderLeft: '4px solid #2196F3' }}>
                              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#2196F3', mb: 1 }}>
                                👥 People
                              </Typography>
                              <List dense>
                                {peopleCauses.map((cause: any, index: number) => (
                                  <ListItem key={index} sx={{ py: 0.5 }}>
                                    <ListItemText 
                                      primary={`• ${cause.text || cause.cause || ''}`}
                                      primaryTypographyProps={{ variant: 'body2' }}
                                    />
                                  </ListItem>
                                ))}
                              </List>
                            </Paper>
                          </Grid>
                        )}
                        
                        {/* Process Causes */}
                        {processCauses.length > 0 && (
                          <Grid item xs={12} md={6}>
                            <Paper sx={{ p: 2, bgcolor: 'rgba(76, 175, 80, 0.05)', borderLeft: '4px solid #4CAF50' }}>
                              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#4CAF50', mb: 1 }}>
                                ⚙️ Process
                              </Typography>
                              <List dense>
                                {processCauses.map((cause: any, index: number) => (
                                  <ListItem key={index} sx={{ py: 0.5 }}>
                                    <ListItemText 
                                      primary={`• ${cause.text || cause.cause || ''}`}
                                      primaryTypographyProps={{ variant: 'body2' }}
                                    />
                                  </ListItem>
                                ))}
                              </List>
                            </Paper>
                          </Grid>
                        )}
                        
                        {/* Policy Causes */}
                        {policyCauses.length > 0 && (
                          <Grid item xs={12} md={6}>
                            <Paper sx={{ p: 2, bgcolor: 'rgba(255, 152, 0, 0.05)', borderLeft: '4px solid #FF9800' }}>
                              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#FF9800', mb: 1 }}>
                                📜 Policy
                              </Typography>
                              <List dense>
                                {policyCauses.map((cause: any, index: number) => (
                                  <ListItem key={index} sx={{ py: 0.5 }}>
                                    <ListItemText 
                                      primary={`• ${cause.text || cause.cause || ''}`}
                                      primaryTypographyProps={{ variant: 'body2' }}
                                    />
                                  </ListItem>
                                ))}
                              </List>
                            </Paper>
                          </Grid>
                        )}
                        
                        {/* Place Causes */}
                        {placeCauses.length > 0 && (
                          <Grid item xs={12} md={6}>
                            <Paper sx={{ p: 2, bgcolor: 'rgba(156, 39, 176, 0.05)', borderLeft: '4px solid #9C27B0' }}>
                              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#9C27B0', mb: 1 }}>
                                📍 Place
                              </Typography>
                              <List dense>
                                {placeCauses.map((cause: any, index: number) => (
                                  <ListItem key={index} sx={{ py: 0.5 }}>
                                    <ListItemText 
                                      primary={`• ${cause.text || cause.cause || ''}`}
                                      primaryTypographyProps={{ variant: 'body2' }}
                                    />
                                  </ListItem>
                                ))}
                              </List>
                            </Paper>
                          </Grid>
                        )}
                        
                        {/* Provision Causes */}
                        {provisionCauses.length > 0 && (
                          <Grid item xs={12} md={6}>
                            <Paper sx={{ p: 2, bgcolor: 'rgba(244, 67, 54, 0.05)', borderLeft: '4px solid #F44336' }}>
                              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#F44336', mb: 1 }}>
                                📦 Provision
                              </Typography>
                              <List dense>
                                {provisionCauses.map((cause: any, index: number) => (
                                  <ListItem key={index} sx={{ py: 0.5 }}>
                                    <ListItemText 
                                      primary={`• ${cause.text || cause.cause || ''}`}
                                      primaryTypographyProps={{ variant: 'body2' }}
                                    />
                                  </ListItem>
                                ))}
                              </List>
                            </Paper>
                          </Grid>
                        )}
                      </Grid>
                    </Box>
                  );
                })()}
              </CardContent>
            </Card>
          )}

          {/* Root Causes Summary */}
          {(() => {
            const rootCauseItems = (project as any).rootCauseItems;
            const prioritizedRootCauses = (project as any).prioritizedRootCauses;
            const peopleCauses = (project as any).peopleCauses || (project as any).people || [];
            const processCauses = (project as any).processCauses || (project as any).process || [];
            const policyCauses = (project as any).policyCauses || (project as any).policy || [];
            const placeCauses = (project as any).placeCauses || (project as any).place || [];
            const provisionCauses = (project as any).provisionCauses || (project as any).provision || [];
            
            // Get prioritized causes from fishbone data
            const prioritizedCauses = [
              ...peopleCauses.filter((c: any) => c.isPrioritized),
              ...processCauses.filter((c: any) => c.isPrioritized),
              ...policyCauses.filter((c: any) => c.isPrioritized),
              ...placeCauses.filter((c: any) => c.isPrioritized),
              ...provisionCauses.filter((c: any) => c.isPrioritized)
            ];
            
            const hasRootCausesData = (rootCauseItems && rootCauseItems.length > 0) || 
                                    (prioritizedRootCauses && prioritizedRootCauses.length > 0) ||
                                    prioritizedCauses.length > 0;
            
            return hasRootCausesData;
          })() && (
            <Card sx={{ mt: 3 }} className="print-page-break print-no-break">
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  🎯 Root Causes Summary
                </Typography>
                <Divider sx={{ mb: 3 }} />
                
                <Grid container spacing={2}>
                  {(() => {
                    const rootCauseItems = (project as any).rootCauseItems;
                    const prioritizedRootCauses = (project as any).prioritizedRootCauses;
                    const peopleCauses = (project as any).peopleCauses || (project as any).people || [];
                    const processCauses = (project as any).processCauses || (project as any).process || [];
                    const policyCauses = (project as any).policyCauses || (project as any).policy || [];
                    const placeCauses = (project as any).placeCauses || (project as any).place || [];
                    const provisionCauses = (project as any).provisionCauses || (project as any).provision || [];
                    
                    // Get prioritized causes from fishbone data
                    const prioritizedCauses = [
                      ...peopleCauses.filter((c: any) => c.isPrioritized).map((c: any) => ({ ...c, category: 'People' })),
                      ...processCauses.filter((c: any) => c.isPrioritized).map((c: any) => ({ ...c, category: 'Process' })),
                      ...policyCauses.filter((c: any) => c.isPrioritized).map((c: any) => ({ ...c, category: 'Policy' })),
                      ...placeCauses.filter((c: any) => c.isPrioritized).map((c: any) => ({ ...c, category: 'Place' })),
                      ...provisionCauses.filter((c: any) => c.isPrioritized).map((c: any) => ({ ...c, category: 'Provision' }))
                    ];
                    
                    let rootCausesToDisplay: any[] = [];
                    
                    if (rootCauseItems && rootCauseItems.length > 0) {
                      rootCausesToDisplay = rootCauseItems
                        .sort((a: any, b: any) => {
                          if (a.rank && b.rank) return a.rank - b.rank;
                          return (b.total || 0) - (a.total || 0);
                        })
                        .slice(0, 5); // Top 5
                    } else if (prioritizedRootCauses && prioritizedRootCauses.length > 0) {
                      rootCausesToDisplay = prioritizedRootCauses
                        .sort((a: any, b: any) => (b.total || 0) - (a.total || 0))
                        .slice(0, 5); // Top 5
                    } else if (prioritizedCauses.length > 0) {
                      rootCausesToDisplay = prioritizedCauses.slice(0, 5); // Top 5
                    }
                    
                    return rootCausesToDisplay.map((cause: any, index: number) => (
                      <Grid item xs={12} key={index}>
                        <Paper sx={{ p: 2, bgcolor: index === 0 ? 'rgba(25, 118, 210, 0.08)' : 'grey.50' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Chip 
                              label={`P${index + 1}`} 
                              color={index === 0 ? 'primary' : 'default'}
                              sx={{ fontWeight: 'bold' }}
                            />
                            <Box sx={{ flexGrow: 1 }}>
                              <Typography variant="body1" sx={{ fontWeight: index === 0 ? 'bold' : 'normal' }}>
                                {cause.rootCause || cause.reason5 || cause.cause || cause.text || 'Root cause not identified'}
                              </Typography>
                              {cause.category && (
                                <Typography variant="caption" color="text.secondary">
                                  Category: {cause.category}
                                </Typography>
                              )}
                              {cause.text && cause.text !== (cause.rootCause || cause.reason5) && (
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                  Original Cause: {cause.text}
                                </Typography>
                              )}
                            </Box>
                            {index === 0 && (
                              <Chip label="Priority" color="primary" size="small" />
                            )}
                          </Box>
                        </Paper>
                      </Grid>
                    ));
                  })()}
                </Grid>
              </CardContent>
            </Card>
          )}

          {/* Action Plan Section */}
          <Card sx={{ mt: 3 }} className="print-page-break print-no-break">
            <CardContent>
              <Typography variant="h6" gutterBottom>
                📋 Action Plan
              </Typography>
              <Divider sx={{ mb: 3 }} />
              
              {/* Latest Action Plan from Progress Update */}
              {project.testCycles && project.testCycles.length > 0 && (() => {
                // Get the most recent test cycle
                const latestCycle = [...project.testCycles]
                  .sort((a, b) => {
                    const cycleA = a as any;
                    const cycleB = b as any;
                    const dateA = a.endDate || a.startDate || cycleA.createdAt || '';
                    const dateB = b.endDate || b.startDate || cycleB.createdAt || '';
                    const timeA = dateA ? new Date(dateA).getTime() : 0;
                    const timeB = dateB ? new Date(dateB).getTime() : 0;
                    return timeB - timeA;
                  })[0];
                
                const cycleData = latestCycle as any;
                const hasLatestPlan = cycleData.actionPlan && Array.isArray(cycleData.actionPlan) && cycleData.actionPlan.length > 0;
                
                if (hasLatestPlan) {
                  return (
                    <Box sx={{ mb: 4 }}>
                      <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                        Latest Project Summary (from most recent progress update)
                      </Typography>
                      <TableContainer component={Paper} variant="outlined" sx={{ mt: 2 }}>
                        <Table>
                          <TableHead>
                            <TableRow sx={{ bgcolor: 'primary.main' }}>
                              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Action</TableCell>
                              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Responsible</TableCell>
                              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Timeline</TableCell>
                              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Resources</TableCell>
                              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>MoV</TableCell>
                              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Status</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {cycleData.actionPlan.map((item: any, index: number) => (
                              <TableRow key={index}>
                                <TableCell>{item.action || item.what || 'Not specified'}</TableCell>
                                <TableCell>{item.responsiblePerson || item.who || 'Not assigned'}</TableCell>
                                <TableCell>{item.byWhen || item.timeline || 'Not specified'}</TableCell>
                                <TableCell>{item.resources || 'Not specified'}</TableCell>
                                <TableCell>{item.mov || 'Not specified'}</TableCell>
                                <TableCell>
                                  <Chip 
                                    label={item.status || 'Not started'} 
                                    size="small"
                                    color={item.status === 'completed' ? 'success' : item.status === 'in_progress' ? 'primary' : 'default'}
                                  />
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Box>
                  );
                }
                return null;
              })()}
              
              {/* Initial Action Plan */}
              {((project as any).actionPlan && Array.isArray((project as any).actionPlan) && (project as any).actionPlan.length > 0) || 
               ((project as any).actionPlanItems && Array.isArray((project as any).actionPlanItems) && (project as any).actionPlanItems.length > 0) ? (
                <Box>
                  <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold', color: 'secondary.main' }}>
                    Initial Project Summary
                  </Typography>
                  <TableContainer component={Paper} variant="outlined" sx={{ mt: 2 }}>
                    <Table>
                      <TableHead>
                        <TableRow sx={{ bgcolor: 'secondary.main' }}>
                          <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Action</TableCell>
                          <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Responsible</TableCell>
                          <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Timeline</TableCell>
                          <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Resources</TableCell>
                          <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>MoV</TableCell>
                          <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Status</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {((project as any).actionPlan || (project as any).actionPlanItems).map((item: any, index: number) => (
                          <TableRow key={index}>
                            <TableCell>{item.action || item.what || 'Not specified'}</TableCell>
                            <TableCell>{item.responsiblePerson || item.who || 'Not assigned'}</TableCell>
                            <TableCell>{item.byWhen || item.timeline || 'Not specified'}</TableCell>
                            <TableCell>{item.resources || 'Not specified'}</TableCell>
                            <TableCell>{item.mov || 'Not specified'}</TableCell>
                            <TableCell>
                              <Chip 
                                label={item.status || 'Not started'} 
                                size="small"
                                color={item.status === 'completed' ? 'success' : item.status === 'in_progress' ? 'primary' : 'default'}
                              />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              ) : (
                <Box sx={{ p: 3, textAlign: 'center', bgcolor: 'background.paper', borderRadius: 1, border: '1px dashed grey.300' }}>
                  <Typography variant="body1" color="textSecondary">
                    No action plan items have been added yet.
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>

        </Paper>
        
        {/* Feedback & Discussion Section */}
        {project && project._id && (
          <FeedbackSection 
            projectId={project._id}
            projectStatus={project.status}
            hasProgressUpdates={project.testCycles && project.testCycles.length > 0}
            showUpdateButton={project.status === 'ongoing' && canUpdateProgress()}
            onUpdateProgress={handleUpdateProgress}
          />
        )}
        
        {/* Action Buttons at Bottom */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-start', gap: 2, mt: 4, mb: 4 }} className="no-print">
          <Tooltip title="Download Project Report">
            <Button
              variant="contained"
              startIcon={<DownloadIcon />}
              onClick={() => window.print()}
              size="large"
            >
              Download
            </Button>
          </Tooltip>
          
          <Tooltip title="Print Project Report">
            <Button
              variant="contained"
              startIcon={<PrintIcon />}
              onClick={() => window.print()}
              size="large"
            >
              Print
            </Button>
          </Tooltip>
          
          <Tooltip title="Close Project Details">
            <Button
              variant="contained"
              color="error"
              startIcon={<CloseIcon />}
              onClick={handleBack}
              size="large"
            >
              Close
            </Button>
          </Tooltip>
        </Box>
        
        {/* AddTestCycle Dialog for Progress Updates */}
        {project && (
          <UpdateProgress
            open={isAddTestCycleOpen}
            projectId={project._id || project.id}
            onClose={handleTestCycleClose}
            onSubmit={handleTestCycleSubmit}
            requiredDate={getNextRequiredUpdate(project)}
          />
        )}
      </Container>
    </>
  );
};

export default ProjectDetail;
